import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface AlertProps {
  tipo: 'success' | 'error' | 'warning' | 'info';
  mensagem: string;
  onClose?: () => void;
  duracao?: number;
}

const IconeSucesso = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-6 w-6 text-white"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const IconeErro = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-6 w-6 text-white"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const IconeAviso = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-6 w-6 text-white"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
  </svg>
);

const IconeInfo = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-6 w-6 text-white"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const IconeFechar = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-5 w-5 text-white hover:text-gray-200 transition-colors duration-200"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const Alert: React.FC<AlertProps> = ({ tipo, mensagem, onClose, duracao = 5000 }) => {
  const [visivel, setVisivel] = useState(true);

  const handleClose = useCallback(() => {
    setVisivel(false);
    if (onClose) {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    if (duracao > 0 && visivel) {
      const timer = setTimeout(() => {
        handleClose();
      }, duracao);
      return () => clearTimeout(timer);
    }
  }, [duracao, handleClose, visivel]);

  const getClassesPorTipo = (tipoAlerta: AlertProps['tipo']) => {
    switch (tipoAlerta) {
      case 'success':
        return {
          bgColor: 'bg-success',
          icone: <IconeSucesso />,
        };
      case 'error':
        return {
          bgColor: 'bg-danger',
          icone: <IconeErro />,
        };
      case 'warning':
        return {
          bgColor: 'bg-warning',
          icone: <IconeAviso />,
        };
      case 'info':
        return {
          bgColor: 'bg-primary',
          icone: <IconeInfo />,
        };
      default:
        return {
          bgColor: 'bg-gray-700',
          icone: <IconeInfo />,
        };
    }
  };

  const { bgColor, icone } = getClassesPorTipo(tipo);

  return (
    <AnimatePresence>
      {visivel && (
        <motion.div
          initial={{ opacity: 0, y: -50, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -50, scale: 0.9 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          role="alert"
          className={`fixed top-4 left-1/2 -translate-x-1/2 z-50 p-4 rounded-lg shadow-xl flex items-center gap-3 max-w-md w-full ${bgColor} text-white font-semibold text-sm md:text-base`}
        >
          <div className="flex-shrink-0">{icone}</div>
          <div className="flex-grow">{mensagem}</div>
          <button
            onClick={handleClose}
            className="flex-shrink-0 p-1 rounded-full hover:bg-white hover:bg-opacity-20 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50"
            aria-label="Fechar alerta"
          >
            <IconeFechar />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Alert;